// Error boundary GLOBAL: última rede de segurança do app. Fica FORA do
// RouterProvider (main.tsx), então pega o que o CatchBoundary do router não
// pega — erro nos providers, na montagem do RouterProvider ou rethrow da tela
// de erro do router. Chunk faltante vira "Atualizando…" + limpeza de cache;
// qualquer outro erro vira uma tela com duas saídas: recarregar ou limpar cache
// e reiniciar.

import { Component, type ErrorInfo, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Logo } from "../brand.js";
import {
  RELOAD_GUARD_KEY,
  clearCacheAndRestart,
  isChunkLoadError,
  readReloadGuard,
  shouldReload,
  writeReloadGuard,
} from "./app-recovery.js";
import i18n from "../i18n.js";

// Tela de erro compartilhada — usada aqui e pela tela de erro do router
// (app-updater), para que a opção de limpar cache exista nos dois caminhos.
export function AppErrorFallback({ error }: { error: unknown }) {
  const { t } = useTranslation();
  const message =
    error instanceof Error ? error.message : String(error ?? i18n.t("appError.unknownError"));
  return (
    <div
      role="alert"
      className="fixed inset-0 z-50 flex flex-col items-center justify-center gap-4 bg-background px-6 text-center text-foreground"
    >
      <p className="text-base font-medium">{t("appError.title")}</p>
      <p className="max-w-md text-sm text-muted-foreground">{t("appError.description")}</p>
      <pre className="max-h-32 max-w-md overflow-auto rounded-md bg-muted px-3 py-2 text-left text-xs text-muted-foreground">
        {message}
      </pre>
      <div className="flex flex-wrap items-center justify-center gap-2">
        <Button onClick={() => location.reload()}>{t("appError.reload")}</Button>
        <Button variant="outline" onClick={() => void clearCacheAndRestart()}>
          {t("appError.clearCache")}
        </Button>
      </div>
    </div>
  );
}

function UpdatingFallback() {
  const { t } = useTranslation();
  return (
    <div
      role="status"
      aria-live="polite"
      className="fixed inset-0 z-50 flex flex-col items-center justify-center gap-4 bg-background px-6 text-center text-sm text-muted-foreground"
    >
      <Logo className="size-20 animate-pulse motion-reduce:animate-none" />
      {t("appError.updating")}
    </div>
  );
}

type Props = { children: ReactNode };
type State = { error: unknown | null; updating: boolean };

export class AppErrorBoundary extends Component<Props, State> {
  override state: State = { error: null, updating: false };

  static getDerivedStateFromError(error: unknown): Partial<State> {
    return { error };
  }

  override componentDidCatch(error: unknown, info: ErrorInfo) {
    console.error("app.error_boundary", error, info.componentStack);
    // Chunk sumido após deploy é "app desatualizado", não bug: limpar o cache e
    // recarregar é exatamente o remédio, então nem mostramos a tela de erro.
    // Guarda anti-loop compartilhada com o app-updater: se o deploy está
    // quebrado, recarregar em loop não conserta — cai na tela de erro normal.
    if (!isChunkLoadError(error)) return;
    const now = Date.now();
    if (!shouldReload(now, readReloadGuard(RELOAD_GUARD_KEY))) return;
    writeReloadGuard(RELOAD_GUARD_KEY, now);
    this.setState({ updating: true });
    void clearCacheAndRestart();
  }

  override render() {
    const { error, updating } = this.state;
    if (error === null) return this.props.children;
    if (updating) return <UpdatingFallback />;
    return <AppErrorFallback error={error} />;
  }
}
